import React, { useEffect, useState, useRef, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { CartContext } from "../context/CartContext";
import { FaEdit, FaArrowLeft } from "react-icons/fa";
import { API_BASE } from "../utils/api";
import { getMyAccountId, isBMI } from "../utils/config";
import { hasAccountAccess } from "../utils/permissions";
import Unauthorized from "./Unauthorized"; // ajusta la ruta si es necesario
import { useLanguage } from "../context/LanguageContext";
import CartBudgetWarning from "../components/CartBudgetWarning";

export default function Checkout() {
    const history = useHistory();
    const location = useLocation();
    const [isProformaMode, setIsProformaMode] = useState(
        new URLSearchParams(location.search).get("mode") === "proforma"
    );
    const [order, setOrder] = useState(null);
    const [agree, setAgree] = useState(false);
    const user = JSON.parse(localStorage.getItem("user"));
    const poNumberRef = useRef(null);
    const notesRef = useRef(null);
    const { clearCart, closeCart } = useContext(CartContext);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const MYACCOUNT_ID = getMyAccountId();
    const { t, switchLanguage, language } = useLanguage();

    // ===================== SYNC MODO DESDE NAVBAR =====================
    useEffect(() => {
        const sync = () => {
            const mode = localStorage.getItem("order_mode") === "proforma";
            setIsProformaMode(mode);
            setAgree(false);
        };
        window.addEventListener("orderModeChange", sync);
        return () => window.removeEventListener("orderModeChange", sync);
    }, []);

    // ===================== CARGA PAYLOAD =====================
    useEffect(() => {
        const saved = localStorage.getItem("checkout_payload");

        if (!saved) {
            history.push(isProformaMode ? "/order-express?mode=proforma" : "/order-express");
            return;
        }

        const parsed = JSON.parse(saved);
        setOrder(parsed);
    }, [history]);

    const confirmOrder = async () => {
        if ((!agree && !isProformaMode) || isSubmitting) return;

        setIsSubmitting(true);

        const payload = JSON.parse(localStorage.getItem("checkout_payload"));
        if (payload.idCustomer === null) {
            alert("Debe seleccionar un cliente");
            return;
        }
        payload.clase = isProformaMode ? " " : (!user?.changeCustomer && user?.groupId === "     1") ? "+" : "*";
        payload.invoiceNumber = poNumberRef.current?.value || "";
        payload.notes = notesRef.current?.value || "";
        payload.branchId = "e";
        try {
            const res = await fetch(
                `${API_BASE}/orders-ultra/api/ws/save_order_express`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                }
            );


            const text = await res.text();
            const data = JSON.parse(text);

            if (!res.ok || data.status !== "OK") {
                throw new Error(data.message || "Error al guardar orden");
            }

            // Al limpiar el carrito se pierde su aporte especulativo al %; refrescar
            // el consumo real para que el indicador de presupuesto no "baje" de golpe.
            window.dispatchEvent(new Event("budget-updated"));

            clearCart();
            localStorage.removeItem("order_express_items");
            localStorage.removeItem("checkout_payload");

            localStorage.setItem(
                "order_confirmation",
                JSON.stringify({
                    orderNumber: data.orderNumber,
                    orderId: data.orderId,
                    orderDate: new Date().toISOString(),
                    isProforma: isProformaMode,
                })
            );

            history.push("/order-confirmation");
        } catch (err) {
            alert(err.message);
            setIsSubmitting(false);
        }
    };

    if (!order) return null;
    // ✅ ESTA LÍNEA ES OBLIGATORIA
    const hasAccess = hasAccountAccess();
    // ✅ render condicional DESPUÉS de hooks
    if (!hasAccess) {
        return <Unauthorized />;
    }

    return (
        <div
            className="w-full min-h-screen px-10 sm:px-6 lg:px-16 py-8 mt-10 transition-colors duration-300"
          
        >
            {/* ====== SWITCH TIPO DOCUMENTO ====== */}


            <div className="space-y-8">

                {/* ===================== GENERAL INFORMATION ===================== */}
                <Section title="General Information" className="hidden" >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <Input label="Company" defaultValue={user?.companyName || ""} disabled />
                        <div></div>
                    </div>

                    <TwoCols>
                        <Input label="First Name" defaultValue={user?.firstName || ""} disabled />
                        <Input label="Last Name" defaultValue={user?.lastName || ""} disabled />
                        <Input label="Email" defaultValue={user?.eMaile || ""} disabled />
                    </TwoCols>
                </Section>

                {/* ===================== BILLING INFORMATION ===================== */}
                <Section title="Billing Information" className="hidden" >
                    <TwoCols>
                        <Input label="Address" defaultValue={user?.address || ""} disabled />
                        <Input label="City" defaultValue={user?.city || ""} disabled />
                        <Input label="Phone" defaultValue={user?.phone || ""} disabled />
                    </TwoCols>
                </Section>

                {/* ===================== SHIPPING INFORMATION ===================== */}
                {!isProformaMode && <Section title={t.shippingInfo}>
                    <TwoCols>
                        <Input
                            label={t.shippingAddress} defaultValue={user?.addressShipTo || user?.address || ""}
                            disabled
                        />

                        <Input
                            label={t.shippingCity} defaultValue={user?.cityShipTo || user?.city || ""}
                            disabled
                        />

                        <Input
                            label={t.shippingPhone} defaultValue={user?.phoneShipTo || user?.phone || ""}
                            disabled
                        />
                    </TwoCols>
                </Section>}


                {/* ===================== ORDER INFORMATION ===================== */}
                {!isProformaMode && <Section title={t.orderInfo}>
                    <TwoCols>
                        <Input
                            label={t.orderPurchaseNumber}
                            maxLength={30}
                            defaultValue={order?.invoiceNumber || user?.nroOrden || ""}
                            inputRef={poNumberRef}
                            disabled={isBMI(user)}
                        />
                        <Textarea
                            label={t.orderPurchaseLogistic}
                            defaultValue={order?.notes || user?.infoDelivery || ""}
                            maxLength={600}
                            textareaRef={notesRef}
                        />
                    </TwoCols>
                </Section>}

                {/* ===================== AVISO DE PRESUPUESTO ===================== */}
                <CartBudgetWarning user={user} cartTotal={order.total} />

                {/* ===================== REVIEW ORDER ===================== */}
                <Section title={isProformaMode ? "Verificar Solicitud de Proforma" : t.reviewOrder}>
                    <div className="bg-white shadow rounded-lg mb-6 overflow-hidden">

                        {/* 🔴 SCROLL ELIMINADO — FLUJO NORMAL */}
                        <table className="w-full min-w-[720px] text-sm">
                            <thead className="bg-gray-100 border-b text-xs">
                                <tr className="text-left align-middle">
                                    <th className="px-2 py-2 w-24 text-center"></th>
                                    <th className="px-2 py-2 w-24">CODIGO</th>
                                    <th className="px-2 py-2">DESCRIPCION</th>
                                    <th className="px-2 py-2 w-16 text-center">CANTIDAD</th>
                                    <th className="px-2 py-2 w-20 text-right">PRECIO</th>
                                    <th className="px-2 py-2 w-16 text-right">IVA</th>
                                    <th className="px-2 py-2 w-24 text-right">SUBTOTAL</th>
                                </tr>
                            </thead>

                            <tbody>
                                {order.items
                                    .filter((x) => x.idProduct != null)
                                    .map((x, i) => (
                                        <tr
                                            key={i}
                                            className="border-b align-middle hover:bg-gray-100 transition"
                                        >
                                            <td className="px-2 py-1.5 text-center">
                                                <img
                                                    src={`https://www.ultra-erp.pro/products/${MYACCOUNT_ID}/${x.code || x.productId}.jpg`}
                                                    className="w-16 h-16 border rounded-md object-cover shadow-sm mx-auto"
                                                    alt={x.code || x.productId}
                                                />
                                            </td>

                                            <td className="px-2 py-1.5">{x.productId || x.code}</td>
                                            <td className="px-2 py-1.5"><span className="px-1 rounded" style={x.notes && x.notes !== (x.description || x.productName) ? { backgroundColor: '#00D2D2' } : {}}>{x.notes || x.description || x.productName}</span></td>
                                            <td className="px-2 py-1.5 text-center">{x.quantity}</td>
                                            <td className="px-2 py-1.5 text-right">
                                                {Number(x.unitPrice || 0).toFixed(4)}
                                            </td>
                                            <td className="px-2 py-1.5 text-right">
                                                {Number(x.taxValue || 0) > 0 ? "*" : ""}
                                            </td>
                                            <td className="px-2 py-1.5 text-right font-bold text-brand-primary">
                                                {Number(x.totalPrice || 0).toFixed(2)}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>

                    {/* MODIFY ORDER + TOTALES */}
                    <div className="flex flex-col sm:flex-row justify-between items-start mt-4 gap-6">

                        <button
                            type="button"
                            onClick={() => history.push(isProformaMode ? "/order-express?mode=proforma" : "/order-express")}
                            className="flex items-center gap-2 px-8 py-3 rounded-lg bg-gray-100 hover:bg-gray-200 font-semibold whitespace-nowrap"
                        >
                            <FaEdit /> {isProformaMode ? "Modificar Solicitud de Proforma" : t.btnModifOrder}
                        </button>


                        <div className="flex justify-end w-full">
                            <div className="w-full max-w-xs sm:max-w-sm bg-white shadow rounded-lg p-5">
                                <div className="flex justify-between mb-2 text-sm">
                                    <span className="font-medium">SubTotal</span>
                                    <span className="font-bold">USD {order.subtotal}</span>
                                </div>

                                <div className="flex justify-between text-base font-bold">
                                    <span>TOTAL</span>
                                    <span className="text-brand-primary text-lg">
                                        USD {order.total}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </Section>

                {/* ===================== PAYMENT INFORMATION ===================== */}
                {!isProformaMode && <Section title={t.paymentInfo}>
                    <Input defaultValue={user?.creditTerms || ""} disabled />
                </Section>}

                {/* ===================== SALES CONDITIONS ===================== */}
                {!isProformaMode && <Section title={t.salesCondition}>
                    <div className="h-32 border rounded p-3 text-sm overflow-y-auto mb-3">
                        <div className="h-32 border rounded p-3 text-sm leading-relaxed overflow-y-auto mb-3 whitespace-pre-line">
                            {t.lblConditionSerec}
                        </div>
                    </div>

                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={agree}
                            onChange={(e) => setAgree(e.target.checked)}
                        />
                        {t.aggreCondition}
                    </label>
                </Section>}


                {/* ===================== CONFIRM BUTTON ===================== */}
                <div className="relative flex items-center justify-center">
                    <button
                        type="button"
                        onClick={() => history.goBack()}
                        className="absolute left-0 flex items-center gap-2 px-6 py-3 
                        rounded-lg bg-gray-100 hover:bg-gray-200 text-sx font-semibold 
                        text-gray-700 transition"
                    >
                        <FaArrowLeft /> Regresar
                    </button>

                    <button
                        disabled={(!agree && !isProformaMode) || isSubmitting}
                        onClick={confirmOrder}
                        className={`
                                flex items-center justify-center gap-3
                                w-full sm:w-80
                                py-3 rounded-lg
                                text-base sm:text-lg font-semibold text-black
                                transition
                                ${(agree || isProformaMode) && !isSubmitting
                                ? isProformaMode
                                    ? "bg-[#FFFFB3] hover:brightness-90"
                                    : "bg-[#80FF80] hover:brightness-90"
                                : "bg-gray-300 text-gray-500 cursor-not-allowed"}
                        `}
                    >
                        {isProformaMode ? "Enviar Solicitud de Proforma" : t.btnConfirOrder}
                    </button>
                </div>

            </div>
        </div>
    );
}

/* ===================== COMPONENTES ===================== */

const Section = ({ title, children, className = "" }) => (
    <section
        className={`
      bg-white
      rounded-lg
      p-6
      border border-gray-200
      shadow-sm
      ${className}
    `}
    >
        <h2 className="text-lg font-semibold mb-4 text-gray-900">
            {title}
        </h2>
        {children}
    </section>
);

const TwoCols = ({ children }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {children}
    </div>
);

const Input = ({
    label,
    defaultValue = "",
    disabled = false,
    inputRef,
    ...props
}) => (
    <div>
        <label className="block text-sm mb-1 text-gray-700">
            {label}
        </label>
        <input
            ref={inputRef}
            type="text"
            defaultValue={defaultValue}
            disabled={disabled}
            {...props}
            className={`
        w-full rounded-md px-3 py-2 text-sm
        bg-white border border-gray-200 uppercase
        focus:outline-none focus:border-brand-primary
        focus:ring-0 transition
        ${disabled ? "bg-gray-100 text-gray-600 cursor-not-allowed" : ""}
      `}
        />
    </div>
);

const Textarea = ({
    label,
    defaultValue = "",
    disabled = false,
    textareaRef,
    ...props
}) => (
    <div>
        <label className="block text-sm mb-1 text-gray-700">
            {label}
        </label>
        <textarea
            ref={textareaRef}
            defaultValue={defaultValue}
            disabled={disabled}
            {...props}
            className={`
        w-full rounded-md px-3 py-2 text-sm
        bg-white border border-gray-200 uppercase
        focus:outline-none focus:border-brand-primary
        focus:ring-0 transition
        ${disabled ? "bg-gray-100 text-gray-600 cursor-not-allowed" : ""}
      `}
            rows={3}
        />
    </div>
);



const Radio = ({ label, ...props }) => (
    <label className="flex items-center gap-2 text-sm">
        <input type="radio" {...props} />
        {label}
    </label>
);
